import React, { useState, useEffect, useRef } from 'react';
import { Shield, CreditCard, Briefcase, Upload, Check, X, AlertTriangle, Plus, Calendar, Building2, Trash2, Edit2 } from 'lucide-react';
import { api, apiFormData, API_BASE } from '../../utils/api';
import DriverComplianceDetail from './DriverComplianceDetail';

const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const CLASSES = ['C', 'LR', 'MR', 'HR', 'HC', 'MC', 'R'];
const EMP_TYPES = [
  { value: 'EMPLOYEE', label: 'Employee' },
  { value: 'OWNER_OPERATOR', label: 'Owner/Operator' },
  { value: 'LABOUR_HIRE', label: 'Labour Hire' },
];

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

const DriverProfileTabs = ({ driverId, driverName, complianceState, canVerify = false }) => {
  const [tab, setTab] = useState('compliance');
  const [licence, setLicence] = useState(null);
  const [licenceLoading, setLicenceLoading] = useState(false);
  const [employment, setEmployment] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [editLicence, setEditLicence] = useState(false);
  const [licenceForm, setLicenceForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [showAddEmp, setShowAddEmp] = useState(false);
  const [empForm, setEmpForm] = useState({ employment_type: 'EMPLOYEE', start_date: '' });
  const frontRef = useRef(null);
  const backRef = useRef(null);

  useEffect(() => {
    if (tab === 'licence') loadLicence();
    if (tab === 'employment') loadEmployment();
  }, [tab, driverId]);

  const loadLicence = async () => {
    setLicenceLoading(true);
    try {
      const data = await api(`/driver-profiles/${driverId}/licence`);
      setLicence(data.licence);
      if (data.licence) setLicenceForm(data.licence);
    } catch { /* table missing */ }
    finally { setLicenceLoading(false); }
  };

  const loadEmployment = async () => {
    setEmpLoading(true);
    try {
      const data = await api(`/driver-profiles/${driverId}/employment`);
      setEmployment(data.history || []);
    } catch { /* table missing */ }
    finally { setEmpLoading(false); }
  };

  const saveLicence = async () => {
    setSaving(true);
    try {
      await api(`/driver-profiles/${driverId}/licence`, {
        method: 'PUT',
        body: JSON.stringify({
          licence_number: licenceForm.licence_number || null,
          issuing_state: licenceForm.issuing_state || null,
          licence_class: licenceForm.licence_class || null,
          issue_date: licenceForm.issue_date || null,
          expiry_date: licenceForm.expiry_date || null,
          conditions: licenceForm.conditions || null,
        }),
      });
      await loadLicence();
      setEditLicence(false);
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const uploadPhoto = async (side, file) => {
    if (!file) return;
    setUploading(side);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await apiFormData(`/driver-profiles/${driverId}/licence/photo/${side}`, formData);
      await loadLicence();
    } catch { /* ignore */ }
    finally { setUploading(null); }
  };

  const verifyLicence = async () => {
    try {
      await api(`/driver-profiles/${driverId}/licence/verify`, { method: 'POST' });
      await loadLicence();
    } catch { /* ignore */ }
  };

  const addEmployment = async () => {
    if (!empForm.start_date || !empForm.employment_type) return;
    setSaving(true);
    try {
      await api(`/driver-profiles/${driverId}/employment`, {
        method: 'POST',
        body: JSON.stringify(empForm),
      });
      setShowAddEmp(false);
      setEmpForm({ employment_type: 'EMPLOYEE', start_date: '' });
      await loadEmployment();
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const deleteEmployment = async (empId) => {
    try {
      await api(`/driver-profiles/${driverId}/employment/${empId}`, { method: 'DELETE' });
      await loadEmployment();
    } catch { /* ignore */ }
  };

  const tabs = [
    { id: 'compliance', label: 'Compliance', icon: Shield },
    { id: 'licence', label: 'Licence', icon: CreditCard },
    { id: 'employment', label: 'Employment', icon: Briefcase },
  ];

  const expiryWarning = licence?.expiry_date && new Date(licence.expiry_date) < new Date(Date.now() + 30 * 86400000);
  const expiryExpired = licence?.expiry_date && new Date(licence.expiry_date) < new Date();
  const photoUrl = (side) => {
    return `${API_BASE}/api/driver-profiles/${driverId}/licence/photo/${side}`;
  };

  return (
    <div className="w-full" data-testid="driver-profile-tabs">
      {/* Tab Nav */}
      <div className="flex gap-1 mb-3 border-b border-slate-700/50 pb-2">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              tab === t.id
                ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
            }`}
            data-testid={`profile-tab-${t.id}`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
            {t.id === 'licence' && licence && !licence.verified && (
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            )}
          </button>
        ))}
      </div>

      {/* Compliance Tab */}
      {tab === 'compliance' && (
        complianceState ? <DriverComplianceDetail state={complianceState} /> : (
          <div className="text-sm text-slate-500 py-4">No compliance state available.</div>
        )
      )}

      {/* Licence Tab */}
      {tab === 'licence' && (
        <div className="space-y-3" data-testid="licence-tab-content">
          {licenceLoading ? (
            <div className="flex items-center gap-2 py-4">
              <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-slate-400">Loading licence...</span>
            </div>
          ) : (
            <>
              {/* Licence Details */}
              {editLicence || !licence ? (
                <div className="bg-slate-900/50 rounded-xl p-3 border border-slate-700/50 space-y-3">
                  <div className="text-xs text-slate-400 font-medium">{licence ? 'Edit Licence' : 'Add Licence Details'}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] text-slate-500 block mb-0.5">Licence Number</label>
                      <input
                        value={licenceForm.licence_number || ''}
                        onChange={e => setLicenceForm(f => ({ ...f, licence_number: e.target.value }))}
                        className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white"
                        data-testid="licence-number-input"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 block mb-0.5">State</label>
                      <select
                        value={licenceForm.issuing_state || ''}
                        onChange={e => setLicenceForm(f => ({ ...f, issuing_state: e.target.value }))}
                        className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white"
                        data-testid="licence-state-select"
                      >
                        <option value="">Select</option>
                        {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 block mb-0.5">Class</label>
                      <select
                        value={licenceForm.licence_class || ''}
                        onChange={e => setLicenceForm(f => ({ ...f, licence_class: e.target.value }))}
                        className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white"
                        data-testid="licence-class-select"
                      >
                        <option value="">Select</option>
                        {CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 block mb-0.5">Issue Date</label>
                      <input
                        type="date"
                        value={licenceForm.issue_date || ''}
                        onChange={e => setLicenceForm(f => ({ ...f, issue_date: e.target.value }))}
                        className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 block mb-0.5">Expiry Date</label>
                      <input
                        type="date"
                        value={licenceForm.expiry_date || ''}
                        onChange={e => setLicenceForm(f => ({ ...f, expiry_date: e.target.value }))}
                        className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white"
                        data-testid="licence-expiry-input"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 block mb-0.5">Conditions</label>
                      <input
                        value={licenceForm.conditions || ''}
                        onChange={e => setLicenceForm(f => ({ ...f, conditions: e.target.value }))}
                        className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white"
                        placeholder="e.g. S, I"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={saveLicence} disabled={saving}
                      className="px-3 py-1.5 bg-blue-500 text-white text-xs rounded-lg hover:bg-blue-600 disabled:opacity-50"
                      data-testid="licence-save-btn"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    {licence && (
                      <button onClick={() => setEditLicence(false)} className="px-3 py-1.5 text-slate-400 text-xs hover:text-white">
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-slate-900/50 rounded-xl p-3 border border-slate-700/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-blue-400" />
                      <span className="text-xs text-white font-medium">Licence Details</span>
                      {licence.verified && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 text-[9px] font-semibold rounded-full">
                          <Check className="w-2.5 h-2.5" /> Verified
                        </span>
                      )}
                      {!licence.verified && (
                        <span className="px-1.5 py-0.5 bg-amber-500/15 text-amber-400 text-[9px] font-semibold rounded-full">
                          Unverified
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      {canVerify && !licence.verified && (
                        <button onClick={verifyLicence}
                          className="px-2 py-1 bg-emerald-500/15 text-emerald-400 text-[10px] font-medium rounded-lg hover:bg-emerald-500/25"
                          data-testid="verify-licence-btn"
                        >
                          <Check className="w-3 h-3 inline mr-0.5" /> Verify
                        </button>
                      )}
                      <button onClick={() => { setLicenceForm(licence); setEditLicence(true); }}
                        className="p-1 text-slate-400 hover:text-white rounded"
                        data-testid="edit-licence-btn"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    <div>
                      <div className="text-[9px] text-slate-500">Number</div>
                      <div className="text-xs text-white font-mono">{licence.licence_number || '-'}</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-slate-500">State</div>
                      <div className="text-xs text-white">{licence.issuing_state || '-'}</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-slate-500">Class</div>
                      <div className="text-xs text-white font-semibold">{licence.licence_class || '-'}</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-slate-500">Issued</div>
                      <div className="text-xs text-white">{fmtDate(licence.issue_date)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-slate-500">Expires</div>
                      <div className={`text-xs font-medium ${expiryExpired ? 'text-red-400' : expiryWarning ? 'text-amber-400' : 'text-white'}`}>
                        {fmtDate(licence.expiry_date)}
                        {expiryExpired && <AlertTriangle className="w-3 h-3 inline ml-0.5 text-red-400" />}
                        {expiryWarning && !expiryExpired && <AlertTriangle className="w-3 h-3 inline ml-0.5 text-amber-400" />}
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] text-slate-500">Conditions</div>
                      <div className="text-xs text-white">{licence.conditions || '-'}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Photo Upload Area */}
              <div className="grid grid-cols-2 gap-3">
                {['front', 'back'].map(side => (
                  <div key={side} className="bg-slate-900/50 rounded-xl border border-slate-700/50 overflow-hidden" data-testid={`licence-photo-${side}`}>
                    <div className="text-[10px] text-slate-500 font-medium px-3 pt-2 capitalize">{side}</div>
                    {licence?.[`photo_${side}_path`] ? (
                      <div className="relative group">
                        <img
                          src={photoUrl(side)}
                          alt={`Licence ${side}`}
                          className="w-full h-32 object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                          <button
                            onClick={() => side === 'front' ? frontRef.current?.click() : backRef.current?.click()}
                            className="px-2 py-1 bg-slate-800 text-white text-[10px] rounded-lg"
                          >
                            Replace
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => side === 'front' ? frontRef.current?.click() : backRef.current?.click()}
                        className="w-full h-28 flex flex-col items-center justify-center gap-1 text-slate-500 hover:text-blue-400 hover:bg-slate-800/50 transition"
                        data-testid={`upload-${side}-btn`}
                      >
                        {uploading === side ? (
                          <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-5 h-5" />
                            <span className="text-[10px]">Upload {side}</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <input ref={frontRef} type="file" accept="image/*" className="hidden" onChange={e => uploadPhoto('front', e.target.files[0])} />
              <input ref={backRef} type="file" accept="image/*" className="hidden" onChange={e => uploadPhoto('back', e.target.files[0])} />
            </>
          )}
        </div>
      )}

      {/* Employment Tab */}
      {tab === 'employment' && (
        <div className="space-y-3" data-testid="employment-tab-content">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 font-medium">Employment History</span>
            <button
              onClick={() => setShowAddEmp(true)}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-blue-400 hover:text-blue-300 bg-blue-500/10 rounded-lg"
              data-testid="add-employment-btn"
            >
              <Plus className="w-3 h-3" /> Add Record
            </button>
          </div>

          {/* Add Form */}
          {showAddEmp && (
            <div className="bg-slate-900/50 rounded-xl p-3 border border-blue-500/30 space-y-2">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">Type</label>
                  <select
                    value={empForm.employment_type}
                    onChange={e => setEmpForm(f => ({ ...f, employment_type: e.target.value }))}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white"
                    data-testid="emp-type-select"
                  >
                    {EMP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">Start Date</label>
                  <input
                    type="date"
                    value={empForm.start_date}
                    onChange={e => setEmpForm(f => ({ ...f, start_date: e.target.value }))}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white"
                    data-testid="emp-start-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">End Date</label>
                  <input
                    type="date"
                    value={empForm.end_date || ''}
                    onChange={e => setEmpForm(f => ({ ...f, end_date: e.target.value || null }))}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">Client/Company</label>
                  <input
                    value={empForm.client_name || ''}
                    onChange={e => setEmpForm(f => ({ ...f, client_name: e.target.value }))}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white"
                    placeholder="Company name"
                    data-testid="emp-client-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">Position</label>
                  <input
                    value={empForm.position || ''}
                    onChange={e => setEmpForm(f => ({ ...f, position: e.target.value }))}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white"
                    placeholder="e.g. HC Driver"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">Notes</label>
                  <input
                    value={empForm.notes || ''}
                    onChange={e => setEmpForm(f => ({ ...f, notes: e.target.value }))}
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={addEmployment} disabled={saving || !empForm.start_date}
                  className="px-3 py-1.5 bg-blue-500 text-white text-xs rounded-lg hover:bg-blue-600 disabled:opacity-50"
                  data-testid="emp-save-btn"
                >
                  {saving ? 'Saving...' : 'Add'}
                </button>
                <button onClick={() => setShowAddEmp(false)} className="px-3 py-1.5 text-slate-400 text-xs hover:text-white">Cancel</button>
              </div>
            </div>
          )}

          {/* Employment Timeline */}
          {empLoading ? (
            <div className="flex items-center gap-2 py-4">
              <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-slate-400">Loading history...</span>
            </div>
          ) : employment.length === 0 ? (
            <div className="text-center py-6 text-slate-500 text-sm">
              No employment history recorded. Click &quot;Add Record&quot; to start.
            </div>
          ) : (
            <div className="space-y-0">
              {employment.map((emp, idx) => {
                const typeCfg = {
                  EMPLOYEE: { bg: 'bg-blue-500/15', fg: 'text-blue-400', icon: Building2 },
                  OWNER_OPERATOR: { bg: 'bg-emerald-500/15', fg: 'text-emerald-400', icon: Shield },
                  LABOUR_HIRE: { bg: 'bg-amber-500/15', fg: 'text-amber-400', icon: Briefcase },
                }[emp.employment_type] || { bg: 'bg-slate-600/15', fg: 'text-slate-400', icon: Briefcase };
                const Icon = typeCfg.icon;
                const isCurrent = !emp.end_date;

                return (
                  <div key={emp.employment_id} className="flex gap-3" data-testid={`emp-record-${emp.employment_id}`}>
                    {/* Timeline line */}
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full ${typeCfg.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-4 h-4 ${typeCfg.fg}`} />
                      </div>
                      {idx < employment.length - 1 && (
                        <div className="w-px flex-1 bg-slate-700 my-1" />
                      )}
                    </div>
                    {/* Content */}
                    <div className="flex-1 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${typeCfg.bg} ${typeCfg.fg}`}>
                            {emp.employment_type?.replace('_', ' ')}
                          </span>
                          {isCurrent && (
                            <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 text-[9px] font-semibold">CURRENT</span>
                          )}
                        </div>
                        <button
                          onClick={() => deleteEmployment(emp.employment_id)}
                          className="p-1 text-slate-600 hover:text-red-400 rounded transition"
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="text-xs text-white font-medium mt-0.5">{emp.client_name || 'Unknown Company'}</div>
                      {emp.position && <div className="text-[10px] text-slate-400">{emp.position}</div>}
                      <div className="flex items-center gap-1 text-[10px] text-slate-500 mt-0.5">
                        <Calendar className="w-3 h-3" />
                        {fmtDate(emp.start_date)} — {emp.end_date ? fmtDate(emp.end_date) : 'Present'}
                      </div>
                      {emp.notes && <div className="text-[10px] text-slate-500 mt-0.5 italic">{emp.notes}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DriverProfileTabs;
